const CACHE_PREFIX = 'santo-cart-drawer-recommendations';
const LAST_PRODUCTS_KEY = `${CACHE_PREFIX}:_last`;
const LAST_FETCHED_IDS_KEY = `${CACHE_PREFIX}:_last_ids`;

function isVariantAvailable(v) {
  if (!v) return false;
  if (!v.inventory_management) return true;
  if (v.inventory_quantity > 0) return true;
  return v.inventory_policy === 'continue';
}

class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this.handleAddClick = this.handleAddClick.bind(this);
  }

  connectedCallback() {
    // The element is re-created on every cart-section refresh (add /
    // remove / qty). On each connect:
    //  1. Render the most-recent product set immediately, filtered for
    //     the current cart — never a blank carousel.
    //  2. If the cart's product IDs changed since the last fetch, get
    //     fresh recommendations for the new context (one clean swap
    //     when they arrive). Quantity-only changes keep the same IDs,
    //     so they don't refetch.
    // The element persists across drawer open/close (open/close does
    // not re-create it), so reopening the drawer never re-renders —
    // that's what eliminates the "recs load in again" flash.
    const hasPlaceholder = this.renderFromLastIfAvailable();
    if (this.cartContextChangedSinceLastFetch() || !hasPlaceholder) {
      if (this.hasCache()) {
        this.renderCached();
      } else {
        this.fetchAndRender();
      }
    }
    this.addEventListener('click', this.handleAddClick);
  }

  cartContextChangedSinceLastFetch() {
    const currentIds = Array.from(this.cartProductIds()).sort().join(',');
    const lastIds = sessionStorage.getItem(LAST_FETCHED_IDS_KEY) || '';
    return currentIds !== lastIds;
  }

  dedupeByTitle(products) {
    // Catalog often has the same garment as separate products per color
    // (e.g. "Lido Short" black / cream / waves blue). Rebuy returns each
    // colorway as a distinct recommendation, which fills the carousel
    // with the same item. Collapse by normalized title so only the
    // first-ranked colorway shows.
    const seen = new Set();
    return products.filter((p) => {
      const key = (p.title || '').trim().toLowerCase();
      if (!key) return true;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  renderFromLastIfAvailable() {
    const drawer = this.closest('cart-drawer');
    if (drawer?.classList.contains('is-empty')) {
      this.hidden = true;
      return false;
    }
    let cached = [];
    try {
      cached = JSON.parse(sessionStorage.getItem(LAST_PRODUCTS_KEY) || '[]');
    } catch {}
    const filtered = this.dedupeByTitle(cached.filter((p) => !this.cartProductIds().has(String(p.id))));
    if (!filtered.length) return false;
    this.hidden = false;
    this.render(filtered);
    return true;
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleAddClick);
  }

  cacheKey() {
    const ids = Array.from(this.cartProductIds()).sort();
    return `${CACHE_PREFIX}:${ids.join(',')}`;
  }

  hasCache() {
    return Boolean(sessionStorage.getItem(this.cacheKey()));
  }

  renderCached() {
    const drawer = this.closest('cart-drawer');
    if (drawer?.classList.contains('is-empty')) {
      this.hidden = true;
      return;
    }
    let cached = [];
    try {
      cached = JSON.parse(sessionStorage.getItem(this.cacheKey()) || '[]');
    } catch {}
    const filtered = this.dedupeByTitle(cached.filter((p) => !this.cartProductIds().has(String(p.id))));
    if (!filtered.length) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    this.render(filtered);
    // Keep the "last products" pointer and the fetched-context marker
    // fresh so future re-creations have up-to-date fallback content and
    // cartContextChangedSinceLastFetch compares against the right cart.
    try {
      sessionStorage.setItem(LAST_PRODUCTS_KEY, JSON.stringify(cached));
      sessionStorage.setItem(LAST_FETCHED_IDS_KEY, Array.from(this.cartProductIds()).sort().join(','));
    } catch {}
  }

  cartProductIds() {
    return new Set(
      (this.dataset.cartProductIds || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }

  async waitForRebuy(maxMs = 3000) {
    const start = Date.now();
    while (!window.Rebuy?.shop?.api_key && Date.now() - start < maxMs) {
      await new Promise((r) => setTimeout(r, 100));
    }
    return Boolean(window.Rebuy?.shop?.api_key);
  }

  async fetchAndRender() {
    const ready = await this.waitForRebuy();
    if (!ready) {
      this.hidden = true;
      return;
    }
    const apiKey = window.Rebuy.shop.api_key;
    const ids = Array.from(this.cartProductIds());
    if (!ids.length) {
      this.hidden = true;
      return;
    }
    const url = new URL('https://rebuyengine.com/api/v1/products/recommended');
    url.searchParams.set('key', apiKey);
    url.searchParams.set('shopify_product_ids', ids.join(','));
    // Ask for extra candidates because dedupeByTitle drops same-title
    // products (e.g. "Lido Short" colorways). 12 leaves headroom for
    // the typical 6-tile carousel.
    url.searchParams.set('limit', '24');
    url.searchParams.set('filter_oos', 'yes');

    let products = [];
    try {
      const res = await fetch(url.toString());
      const json = await res.json();
      const raw = (json.data || []).filter((p) => !this.cartProductIds().has(String(p.id)));
      products = this.dedupeByTitle(raw).slice(0, 6);
    } catch (err) {
      console.error('Rebuy recommendations failed', err);
    }

    if (!products.length) {
      sessionStorage.removeItem(this.cacheKey());
      this.hidden = true;
      return;
    }
    try {
      sessionStorage.setItem(this.cacheKey(), JSON.stringify(products));
      // Mirror to a generic "last products" key so the next instance
      // (re-created during a cart update) can render immediately
      // instead of waiting for the next fetch.
      sessionStorage.setItem(LAST_PRODUCTS_KEY, JSON.stringify(products));
      // Record the cart context these recs were fetched for, so the
      // next connectedCallback can detect "cart changed since last
      // fetch" and trigger a fresh fetch.
      sessionStorage.setItem(LAST_FETCHED_IDS_KEY, Array.from(this.cartProductIds()).sort().join(','));
    } catch {}
    this.hidden = false;
    this.render(products);
  }

  render(products) {
    this.products = products;
    const heading = this.dataset.heading || 'You may also like';
    const cards = products.map((p) => this.cardHTML(p)).join('');
    this.innerHTML = `
      <h2 class="cart-drawer-rec__heading">${heading}</h2>
      <div class="cart-drawer-rec__scroller">${cards}</div>
      <p class="cart-drawer-rec__status visually-hidden" role="status" aria-live="polite"></p>
    `;
  }

  announce(message) {
    const status = this.querySelector('.cart-drawer-rec__status');
    if (!status) return;
    status.textContent = '';
    requestAnimationFrame(() => {
      status.textContent = message;
    });
  }

  closePicker(picker) {
    if (!picker) return;
    const cleanup = picker._cleanup;
    if (typeof cleanup === 'function') cleanup();
    const trigger = picker.closest('.cart-drawer-rec__media')?.querySelector('.cart-drawer-rec__add');
    if (trigger) {
      const title = trigger.dataset.productTitle || '';
      trigger.setAttribute('aria-label', title ? `Add ${title} to cart` : 'Add to cart');
      trigger.setAttribute('aria-expanded', 'false');
    }
    picker.remove();
  }

  cardHTML(product) {
    const variant = product.variants.find(isVariantAvailable) || product.variants[0];
    const image = this.safeImageUrl(product.images?.[0]?.src);
    const price = this.formatMoney(variant.price);
    const title = this.escape(product.title);
    const handle = this.escape(product.handle);
    const isMulti = product.variants.length > 1;
    const buttonAttrs = isMulti
      ? `data-product-id="${this.escape(product.id)}"`
      : `data-variant-id="${this.escape(variant.id)}"`;
    return `
      <div class="cart-drawer-rec__card">
        <div class="cart-drawer-rec__media">
          <a href="/products/${handle}" class="cart-drawer-rec__image-link" tabindex="-1" aria-hidden="true">
            <img src="${image}" alt="" class="cart-drawer-rec__image" loading="lazy">
          </a>
          <button type="button" class="cart-drawer-rec__add" ${buttonAttrs} data-product-title="${title}" aria-label="Add ${title} to cart" aria-expanded="false">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 6L12 18" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
              <path d="M18 12L6 12" stroke="currentColor" stroke-width="2" stroke-linecap="square" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
        <a href="/products/${handle}" class="cart-drawer-rec__name">${title}</a>
        <div class="cart-drawer-rec__price">${price}</div>
      </div>
    `;
  }

  safeImageUrl(src) {
    if (!src) return '';
    const trimmed = String(src).trim();
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('//')) return '';
    // Rebuy returns the full-resolution original (~2048px, 200-500KB).
    // The card renders at 144px wide — request a width-capped render
    // from the Shopify CDN so we ship ~20-40KB instead. 400px covers
    // retina (144 * 2.7x) with object-fit: cover.
    let sized = trimmed;
    if (/cdn\.shopify\.com/.test(trimmed) && !/[?&]width=/i.test(trimmed)) {
      sized += (trimmed.includes('?') ? '&' : '?') + 'width=400';
    }
    return this.escape(sized);
  }

  formatMoney(value) {
    const amount = typeof value === 'string' ? parseFloat(value) : value / 100;
    if (Number.isNaN(amount)) return '';
    return `$${amount.toFixed(2).replace(/\.00$/, '')}`;
  }

  escape(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[c]);
  }

  async handleAddClick(event) {
    const sizeBtn = event.target.closest('.cart-drawer-rec__size');
    if (sizeBtn) {
      event.preventDefault();
      event.stopPropagation();
      const picker = sizeBtn.closest('.cart-drawer-rec__sizes');
      const ok = await this.addVariant(sizeBtn.dataset.variantId, sizeBtn);
      if (ok) this.closePicker(picker);
      return;
    }

    const button = event.target.closest('.cart-drawer-rec__add');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    if (button.dataset.productId) {
      const existingPicker = button.closest('.cart-drawer-rec__media').querySelector('.cart-drawer-rec__sizes');
      if (existingPicker) {
        this.closePicker(existingPicker);
        return;
      }
      const product = this.products?.find((p) => String(p.id) === button.dataset.productId);
      if (!product) return;
      // Always show the size picker for multi-variant products — let the
      // user pick their size explicitly instead of auto-guessing from
      // what's already in cart.
      this.openSizePicker(button, product);
      return;
    }
    await this.addVariant(button.dataset.variantId, button);
  }

  async addVariant(variantId, btn) {
    if (!variantId) return false;
    if (btn) btn.disabled = true;
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!res.ok) throw new Error(`Add failed: ${res.status}`);
      document.dispatchEvent(new CustomEvent('rebuy:cart.change', { bubbles: true }));
      return true;
    } catch (err) {
      console.error('Add to cart failed', err);
      this.announce("Couldn't add item. Please try again.");
      if (btn) btn.disabled = false;
      return false;
    }
  }

  openSizePicker(button, product) {
    this.querySelectorAll('.cart-drawer-rec__sizes').forEach((el) => this.closePicker(el));

    const sizeIdx = (product.options || []).findIndex((opt) => {
      const name = typeof opt === 'string' ? opt : opt?.name || '';
      return name.toLowerCase().includes('size');
    });
    if (sizeIdx === -1) return;

    const defaultVariant = product.variants.find(isVariantAvailable) || product.variants[0];
    const sizeKey = `option${sizeIdx + 1}`;
    const variants = product.variants.filter((v) =>
      (product.options || []).every((opt, i) => {
        if (i === sizeIdx) return true;
        return v[`option${i + 1}`] === defaultVariant[`option${i + 1}`];
      }),
    );

    const buttons = variants
      .map((v) => {
        const value = this.escape(v[sizeKey] || '');
        const avail = isVariantAvailable(v);
        return `<button type="button" class="cart-drawer-rec__size" data-variant-id="${v.id}" ${avail ? '' : 'disabled aria-disabled="true"'}>${value}</button>`;
      })
      .join('');

    const picker = document.createElement('div');
    picker.className = 'cart-drawer-rec__sizes';
    picker.setAttribute('role', 'group');
    picker.setAttribute('aria-label', `Select size for ${this.escape(product.title)}`);
    picker.innerHTML = buttons;
    button.closest('.cart-drawer-rec__media').appendChild(picker);
    button.setAttribute('aria-label', 'Close size picker');
    button.setAttribute('aria-expanded', 'true');

    const onOutside = (e) => {
      if (picker.contains(e.target) || e.target.closest('.cart-drawer-rec__add')) return;
      this.closePicker(picker);
    };
    picker._cleanup = () => {
      document.removeEventListener('click', onOutside);
      picker._cleanup = null;
    };
    setTimeout(() => document.addEventListener('click', onOutside), 0);
  }

}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
